import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import type { ScoreRow } from "../components/contracts";
import { Footer } from "../components/Footer";
import { NavBar } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { cx, stagger } from "../components/util";

/** Visual order on the steps: silver, gold, bronze. */
const STEP_ORDER = [1, 0, 2];

export interface PodiumProps {
  playerId: string;
  rows: ScoreRow[];
  /**
   * Starts a fresh room. `convex/**` has no "play again" mutation and the
   * podium phase purges the room's R2 objects, so this deliberately leaves the
   * dead room rather than pretending to reset it.
   */
  onPlayAgain: () => void;
}

export function Podium({ playerId, rows, onPlayAgain }: PodiumProps) {
  const top = rows.slice(0, 3);
  const rest = rows.slice(3);
  const winner = top[0];

  return (
    <div className="bd-screen">
      <NavBar subnav={<span>Final standings</span>} />

      <main className="shell bd-stack-lg">
        {/* The one shimmering surface in the app. It has earned it. */}
        <div className="plate chamfer bd-podium-plate bd-shimmer bd-pop">
          <p className="t-label muted">Badly dubbed champion</p>
          <h1 className="t-display t-outlined bd-podium-winner">
            {winner?.name ?? "Nobody"}
          </h1>
          <p className="t-hero-tagline">{winner?.total ?? 0} points</p>
        </div>

        <div className="plate chamfer bd-podium">
          <div className="bd-podium-cols">
            {STEP_ORDER.map((rankIndex, i) => {
              const row = top[rankIndex];
              if (!row) return <div key={rankIndex} />;
              return (
                <div key={row.playerId} className="bd-podium-col">
                  <span
                    className={cx("bd-podium-name", row.playerId === playerId && "t-link")}
                  >
                    {row.name}
                  </span>
                  <Badge tone={rankIndex === 0 ? "ready" : "carbon"}>{row.total}</Badge>
                  <div
                    className={cx("bd-podium-step", `is-${rankIndex + 1}`, "bd-rise")}
                    style={stagger(i)}
                  >
                    <span aria-hidden="true">{rankIndex + 1}</span>
                    <span className="bd-sr-only">
                      {row.name} finished {rankIndex + 1} with {row.total} points.
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {rest.length > 0 ? (
          <Panel title="Everyone else">
            <div className="bd-stagger">
              {rest.map((row, i) => (
                <div
                  key={row.playerId}
                  className={cx("row", row.playerId === playerId && "row-highlight")}
                  style={stagger(i)}
                >
                  <span className="bd-score-rank" aria-hidden="true">
                    {row.rank}
                  </span>
                  <span className="t-link grow truncate">{row.name}</span>
                  <span className="bd-score-total">{row.total}</span>
                </div>
              ))}
            </div>
          </Panel>
        ) : null}

        <div className="plate chamfer bd-pad-lg center">
          <Button variant="submit" block beacon onClick={onPlayAgain}>
            Play again
          </Button>
          <p className="t-micro muted">
            Starts a fresh room &mdash; the clips from this game are already deleted.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
